import { createApp } from "vue";
import App from "./App.vue";
import router from "./router";
import store from "./store";
import PrimeVue from "primevue/config";
import "/node_modules/primeflex/primeflex.css";
import "primeicons/primeicons.css";
import "./assets/css/main.css";
import "@mdi/font/css/materialdesignicons.min.css";

// Global services
import ToastService from "primevue/toastservice";
import ConfirmationService from "primevue/confirmationservice";

// Global directives
import StyleClass from "primevue/styleclass";
import Tooltip from "primevue/tooltip";
import BadgeDirective from "primevue/badgedirective";

// Global components
import WLoading from "./components/common/WLoading.vue";
import WTitle from "./components/common/WTitle.vue";
import WSubTitle from "./components/common/WSubTitle.vue";
import Chart from "primevue/chart";
import FileUpload from "primevue/fileupload";
import Toolbar from "primevue/toolbar";
import Sidebar from "primevue/sidebar";
import Menu from "primevue/menu";
import Menubar from "primevue/menubar";
import TabMenu from "primevue/tabmenu";
import PanelMenu from "primevue/panelmenu";
import TieredMenu from "primevue/tieredmenu";
import Message from "primevue/message";
import InlineMessage from "primevue/inlinemessage";
import Toast from "primevue/toast";
import Image from "primevue/image";
import Avatar from "primevue/avatar";
import Badge from "primevue/badge";
import BlockUI from "primevue/blockui";
import Chip from "primevue/chip";
import Chips from "primevue/chips";
import Inplace from "primevue/inplace";
import MeterGroup from "primevue/metergroup";
import ScrollTop from "primevue/scrolltop";
import ProgressBar from "primevue/progressbar";
import ProgressSpinner from "primevue/progressspinner";
import Tag from "primevue/tag";
import Button from "primevue/button";
import SplitButton from "primevue/splitbutton";
import Password from "primevue/password";
import InputText from "primevue/inputtext";
import InputNumber from "primevue/inputnumber";
import Textarea from "primevue/textarea";
import Dropdown from "primevue/dropdown";
import Listbox from "primevue/listbox";
import OrderList from "primevue/orderlist";
import Checkbox from "primevue/checkbox";
import InputSwitch from "primevue/inputswitch";
import RadioButton from "primevue/radiobutton";
import Slider from "primevue/slider";
import SelectButton from "primevue/selectbutton";
// Data Table components
import DataTable from "primevue/datatable";
import Column from "primevue/column";
import ColumnGroup from "primevue/columngroup"; // optional
import Row from "primevue/row"; // optional
//
import Paginator from "primevue/paginator";
import Accordion from "primevue/accordion";
import AccordionTab from "primevue/accordiontab";
import Card from "primevue/card";
import Fieldset from "primevue/fieldset";
import Panel from "primevue/panel";
import ScrollPanel from "primevue/scrollpanel";
import Divider from "primevue/divider";
// Splitter
import Splitter from "primevue/splitter";
import SplitterPanel from "primevue/splitterpanel";
// Tabs
import TabView from "primevue/tabview";
import TabPanel from "primevue/tabpanel";
// Dialogs
import ConfirmDialog from "primevue/confirmdialog";
import Dialog from "primevue/dialog";
import OverlayPanel from "primevue/overlaypanel";
// Effecs
import Ripple from "primevue/ripple";
import AuthManager from "./utils/AuthManager";
import Calendar from "primevue/calendar";
import FloatLabel from "primevue/floatlabel";

//Apex Charts
import VueApexCharts from "vue3-apexcharts";

// Initialize the Vue app
const app = createApp(App);

// Init app config
// if (process.env.VUE_APP_DEV != 'true') {
//   //@ts-ignore
//   app.config.productionTip = false;
//   //@ts-ignore
//   app.config.productionTip = false
//   //@ts-ignore
//   app.config.devtools = false
//   //@ts-ignore
//   app.config.silent = true
// }

// Restore a persisted session (if any) by validating the stored token via /me.
await AuthManager.restore();

// Routes guards: enforce authentication, forced password reset, and per-route
// permission requirements (route.meta.permission = "component:action").
router.beforeEach(async (to, from, next) => {
  const isLoggedIn: boolean = AuthManager.isLoggedIn();
  const mustChange: boolean = store.getters.getMustChangePassword;

  if (!isLoggedIn) {
    if (to.name === "login") next();
    else next({ name: "login" });
    return;
  }

  // Logged in but visiting the login page -> send to the right place.
  if (to.name === "login") {
    next({ name: mustChange ? "change-password" : "dashboard" });
    return;
  }

  // Force the first-login password change.
  if (mustChange && to.name !== "change-password") {
    next({ name: "change-password" });
    return;
  }
  if (!mustChange && to.name === "change-password") {
    next({ name: "dashboard" });
    return;
  }

  // Route-level permission gating.
  const required = to.meta && (to.meta as any).permission;
  if (required) {
    const [component, action] = (required as string).split(":");
    if (!store.getters.can(component, action)) {
      next({ name: "dashboard" });
      return;
    }
  }

  next();
});

// Plugins, directives and global components
app.use(store);
app.use(router);
app.use(PrimeVue, { ripple: true });
app.use(ToastService);
app.use(ConfirmationService);
app.use(VueApexCharts);

// Global directives
app.directive("tooltip", Tooltip); // Directive
app.directive("badge", BadgeDirective);
app.directive("styleclass", StyleClass);
app.directive("ripple", Ripple);

// Global components registration
app.component("WLoading", WLoading);
app.component("WTitle", WTitle);
app.component("WSubTitle", WSubTitle);
app.component("PChart", Chart);
app.component("PFileUpload", FileUpload);
app.component("PToolbar", Toolbar);
app.component("PSidebar", Sidebar);
app.component("PMenu", Menu);
app.component("PMenubar", Menubar);
app.component("PTabMenu", TabMenu);
app.component("PPanelMenu", PanelMenu);
app.component("PTieredMenu", TieredMenu);
app.component("PMessage", Message);
app.component("PInlineMessage", InlineMessage);
app.component("PToast", Toast);
app.component("PImage", Image);
app.component("PAvatar", Avatar);
app.component("PBadge", Badge);
app.component("PBlockUI", BlockUI);
app.component("PChip", Chip);
app.component("PChips", Chips);
app.component("PInplace", Inplace);
app.component("PMeterGroup", MeterGroup);
app.component("PScrollTop", ScrollTop);
app.component("PProgressBar", ProgressBar);
app.component("PProgressSpinner", ProgressSpinner);
app.component("PTag", Tag);
app.component("PButton", Button);
app.component("PSplitButton", SplitButton);
app.component("PPassword", Password);
app.component("PInputText", InputText);
app.component("PInputNumber", InputNumber);
app.component("PTextarea", Textarea);
app.component("PDropdown", Dropdown);
app.component("PListbox", Listbox);
app.component("POrderList", OrderList);
app.component("PCheckbox", Checkbox);
app.component("PInputSwitch", InputSwitch);
app.component("PRadioButton", RadioButton);
app.component("PSlider", Slider);
app.component("PSelectButton", SelectButton);
app.component("PDataTable", DataTable);
app.component("PColumn", Column);
app.component("PColumnGroup", ColumnGroup);
app.component("PRow", Row);
app.component("PPaginator", Paginator);
app.component("PAccordion", Accordion);
app.component("PAccordionTab", AccordionTab);
app.component("PCard", Card);
app.component("PFieldset", Fieldset);
app.component("PPanel", Panel);
app.component("PScrollPanel", ScrollPanel);
app.component("PDivider", Divider);
app.component("PSplitter", Splitter);
app.component("PSplitterPanel", SplitterPanel);
app.component("PTabView", TabView);
app.component("PTabPanel", TabPanel);
app.component("PConfirmDialog", ConfirmDialog);
app.component("PDialog", Dialog);
app.component("POverlayPanel", OverlayPanel);
app.component("PCalendar", Calendar);
app.component("PFloatLabel", FloatLabel);

// mount app
app.mount("#app");

export default {
  app,
  config: app.config.globalProperties,
};
